<script setup lang="ts">
import { computed } from 'vue';
import type { LuggageInfo, ArrangementPhotoInfo, ReceptionPhotoInfo } from '@/entities/application/model/types';
import type { LuggageLocationResponse } from '@/entities/application/api/fetchLuggageLocation';
import { formatDateTimeToJst } from '@/entities/application/lib/datetime';

type LuggageLocation = LuggageLocationResponse['luggages'][number];

export type Props = {
    luggageInfo?: LuggageInfo;
    arrangementPhotoInfo?: ArrangementPhotoInfo;
    receptionPhotoInfo?: ReceptionPhotoInfo;
    luggages?: readonly LuggageLocation[];
    defaultOpen?: boolean;
    disabled?: boolean;
    showManageButtons?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    luggageInfo: () => ({ quantity: 0, expected_quantity: 0, luggage_qr_codes: [] }),
    arrangementPhotoInfo: () => ({ arrangement_photos: [] }),
    receptionPhotoInfo: () => ({ reception_photos: [] }),
    luggages: () => [],
    defaultOpen: false,
    disabled: false,
    showManageButtons: true,
});

const emit = defineEmits<{
    'add-luggage': [];
    'remove-luggage': [];
    'photo-click': [photoUrl: string, photoType: 'reception' | 'arrangement'];
}>();

const luggageQuantity = computed(() => props.luggageInfo?.quantity || 0);
const expectedQuantity = computed(() => props.luggageInfo?.expected_quantity || 0);
const arrangementPhotos = computed(() => props.arrangementPhotoInfo?.arrangement_photos || []);
const receptionPhotos = computed(() => props.receptionPhotoInfo?.reception_photos || []);

// 現在ロケーション表示用（例: デポ→トラックB）
const formatCurrentLocation = (luggage: LuggageLocation): string => {
    const { location_name, vehicle_name } = luggage.current_location;
    if (vehicle_name) {
        return `${location_name}→${vehicle_name}`;
    }
    return location_name;
};

// 荷物追加ボタンクリック
const handleAddLuggage = () => {
    emit('add-luggage');
};

// 荷物削除ボタンクリック
const handleRemoveLuggage = () => {
    emit('remove-luggage');
};
</script>

<template>
    <v-expansion-panels :model-value="defaultOpen ? [0] : []" class="mb-4">
        <v-expansion-panel>
            <v-expansion-panel-title :disabled="disabled">
                <div class="d-flex align-center">
                    <v-icon class="me-2">mdi-package-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">荷物情報</span>
                    <v-chip v-if="luggageQuantity > 0" size="small" color="primary" class="ms-2">
                        {{ luggageQuantity }}個
                    </v-chip>
                </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
                <div class="pa-2">
                    <!-- 荷物管理ボタン -->
                    <div v-if="showManageButtons" class="luggage-buttons d-flex mb-4">
                        <v-btn
                            color="primary"
                            variant="outlined"
                            prepend-icon="mdi-plus"
                            size="small"
                            @click="handleAddLuggage"
                        >
                            荷物追加
                        </v-btn>
                        <v-btn
                            color="error"
                            variant="outlined"
                            prepend-icon="mdi-minus"
                            size="small"
                            :disabled="luggageQuantity === 0"
                            @click="handleRemoveLuggage"
                        >
                            荷物削除
                        </v-btn>
                    </div>

                    <!-- 荷物数表示 -->
                    <v-row class="mb-3">
                        <v-col cols="12">
                            <div class="d-flex align-center mb-1">
                                <v-icon class="me-2" color="primary">mdi-counter</v-icon>
                                <span class="text-body-1">想定荷物数: </span>
                                <span class="text-h6 font-weight-bold ms-2"> {{ expectedQuantity }}個 </span>
                            </div>
                            <div class="d-flex align-center">
                                <v-icon class="me-2" color="primary">mdi-package-variant-closed-check</v-icon>
                                <span class="text-body-1">実際荷物数: </span>
                                <span class="text-h6 font-weight-bold text-primary ms-2">
                                    {{ luggageQuantity }}個
                                </span>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- 荷物一覧テーブル -->
                    <div v-if="luggages.length > 0">
                        <v-table density="compact" class="luggage-table desktop-only">
                            <thead>
                                <tr>
                                    <th class="text-left">荷物QRコード</th>
                                    <th class="text-left">現在ロケーション</th>
                                    <th class="text-left">荷物スキャン</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="luggage in luggages" :key="luggage.luggage_id">
                                    <td>
                                        <v-chip
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                            prepend-icon="mdi-qrcode-scan"
                                        >
                                            {{ luggage.luggage_qr_code }}
                                        </v-chip>
                                    </td>
                                    <td>{{ formatCurrentLocation(luggage) }}</td>
                                    <td>{{ formatDateTimeToJst(luggage.current_location.recorded_at) }}</td>
                                </tr>
                            </tbody>
                        </v-table>

                        <div class="mobile-cards mobile-only">
                            <div v-for="luggage in luggages" :key="luggage.luggage_id" class="luggage-card">
                                <div class="luggage-card__row">
                                    <span class="cell-sub">荷物QRコード</span>
                                    <v-chip
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        prepend-icon="mdi-qrcode-scan"
                                    >
                                        {{ luggage.luggage_qr_code }}
                                    </v-chip>
                                </div>
                                <div class="luggage-card__row">
                                    <span class="cell-sub">現在ロケーション</span>
                                    <span class="cell-main">{{ formatCurrentLocation(luggage) }}</span>
                                </div>
                                <div class="luggage-card__row">
                                    <span class="cell-sub">荷物スキャン</span>
                                    <span class="cell-main">{{
                                        formatDateTimeToJst(luggage.current_location.recorded_at)
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 荷物がない場合の表示 -->
                    <div v-else class="text-center py-4">
                        <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-package-variant-remove</v-icon>
                        <p class="text-body-2 text-grey">登録された荷物がありません</p>
                    </div>

                    <!-- 受付写真 -->
                    <div v-if="receptionPhotos.length > 0" class="mt-4">
                        <div class="d-flex align-center mb-3">
                            <v-icon class="me-2" color="primary">mdi-camera</v-icon>
                            <span class="text-body-1 font-weight-medium">荷物写真(最大3枚)</span>
                        </div>

                        <div class="photo-gallery">
                            <v-img
                                v-for="(photo, index) in receptionPhotos"
                                :key="`reception-${index}`"
                                :src="photo"
                                :alt="`荷物写真 ${index + 1}`"
                                class="photo-item"
                                cover
                                @click="$emit('photo-click', photo, 'reception')"
                            >
                                <template #placeholder>
                                    <div class="d-flex align-center justify-center h-100">
                                        <v-progress-circular indeterminate color="primary" />
                                    </div>
                                </template>

                                <template #error>
                                    <div class="d-flex align-center justify-center h-100 bg-grey-lighten-4">
                                        <v-icon size="32" color="grey">mdi-image-broken</v-icon>
                                    </div>
                                </template>
                            </v-img>
                        </div>
                    </div>

                    <!-- 置き配写真 -->
                    <div v-if="arrangementPhotos.length > 0" class="mt-4">
                        <div class="d-flex align-center mb-3">
                            <v-icon class="me-2" color="primary">mdi-camera</v-icon>
                            <span class="text-body-1 font-weight-medium">置き配写真</span>
                        </div>

                        <div class="photo-gallery">
                            <v-img
                                v-for="(photo, index) in arrangementPhotos"
                                :key="index"
                                :src="photo"
                                :alt="`置き配写真 ${index + 1}`"
                                class="photo-item"
                                cover
                                @click="$emit('photo-click', photo, 'arrangement')"
                            >
                                <template #placeholder>
                                    <div class="d-flex align-center justify-center h-100">
                                        <v-progress-circular indeterminate color="primary" />
                                    </div>
                                </template>

                                <template #error>
                                    <div class="d-flex align-center justify-center h-100 bg-grey-lighten-4">
                                        <v-icon size="32" color="grey">mdi-image-broken</v-icon>
                                    </div>
                                </template>
                            </v-img>
                        </div>
                    </div>
                </div>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<style scoped>
.luggage-buttons {
    gap: 24px;
}

.luggage-table {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.luggage-table th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
}

.desktop-only {
    display: block;
}

.mobile-only {
    display: none !important;
}

@media (max-width: 600px) {
    .desktop-only {
        display: none !important;
    }

    .mobile-only {
        display: flex !important;
        flex-direction: column;
    }

    :deep(.v-expansion-panel-text__wrapper) {
        padding-inline: 8px;
    }
}

.mobile-cards {
    display: flex;
    flex-direction: column;
}

.luggage-card {
    background: #fff;
    padding: 12px 0;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 8px;
}

.luggage-card:last-child {
    margin-bottom: 0;
}

.luggage-card__row {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.luggage-card__row + .luggage-card__row {
    margin-top: 8px;
}

.cell-main {
    font-size: 0.875rem;
    font-weight: 500;
    color: #1a1a1a;
    line-height: 1.4;
}

.cell-sub {
    font-size: 0.8125rem;
    color: #9e9e9e;
    line-height: 1.4;
}

.photo-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
}

.photo-item {
    height: 150px;
    border-radius: 8px;
    cursor: pointer;
    position: relative;
    transition: transform 0.2s ease;
}

.photo-item:hover {
    transform: scale(1.02);
}
</style>
